import React, { useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';

const AgentList = memo(function AgentList({ agents, onManage }) {
  const [copiedAgentId, setCopiedAgentId] = useState(null);
  const [showEmbedCode, setShowEmbedCode] = useState(null);
  const navigate = useNavigate();

  const copyEmbedCode = (agentId) => {
    const embedCode = `<script src="https://bytebot.bytesizeds.com/agent.js" data-agent-id="${agentId}" defer></script>`;
    navigator.clipboard.writeText(embedCode);
    setCopiedAgentId(agentId);
    setShowEmbedCode(agentId);
  };

  const handleRefineAgent = (agentId) => {
    // Navigate to the agent page with the ByteBot Builder tab
    navigate(`/dashboard/agent/${agentId}?tab=builder`);
  };

  const styles = {
    container: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
      gap: '30px',
      marginTop: '20px',
    },
    card: {
      backgroundColor: '#fff',
      border: '2px solid #333',
      borderRadius: '8px',
      overflow: 'hidden',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    },
    cardHover: {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
    },
    cardHeader: {
      backgroundColor: '#333',
      color: '#fff',
      padding: '20px',
      borderBottom: '2px solid #000',
    },
    agentName: {
      fontSize: '1.3rem',
      fontWeight: 'bold',
      margin: '0 0 8px 0',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
    cardBody: {
      padding: '20px',
    },
    promptSection: {
      marginBottom: '20px',
    },
    sectionTitle: {
      fontSize: '0.9rem',
      fontWeight: 'bold',
      color: '#333',
      marginBottom: '8px',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
    promptText: {
      backgroundColor: '#f8f9fa',
      border: '1px solid #ddd',
      borderRadius: '4px',
      padding: '12px',
      fontSize: '0.85rem',
      color: '#555',
      lineHeight: '1.5',
      maxHeight: '100px',
      overflow: 'hidden',
      position: 'relative',
    },
    promptFade: {
      position: 'absolute',
      bottom: '0',
      left: '0',
      right: '0',
      height: '20px',
      background: 'linear-gradient(transparent, #f8f9fa)',
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap',
    },
    button: {
      padding: '10px 15px',
      border: '2px solid #333',
      borderRadius: '4px',
      fontSize: '0.8rem',
      fontWeight: 'bold',
      fontFamily: 'inherit',
      backgroundColor: '#fff',
      color: '#333',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      flex: '1',
      textAlign: 'center',
      minWidth: '100px',
    },
    buttonPrimary: {
      backgroundColor: '#333',
      color: '#fff',
    },
    buttonSuccess: {
      backgroundColor: '#4caf50',
      borderColor: '#4caf50',
      color: '#fff',
    },
    embedSection: {
      marginTop: '15px',
      padding: '15px',
      backgroundColor: '#f0f8ff',
      border: '1px solid #007acc',
      borderRadius: '4px',
    },
    embedCode: {
      fontFamily: "'Monaco', 'Menlo', monospace",
      fontSize: '0.75rem',
      backgroundColor: '#333',
      color: '#fff',
      padding: '10px',
      borderRadius: '4px',
      marginTop: '8px',
      wordBreak: 'break-all',
      lineHeight: '1.4',
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#666',
    },
    emptyTitle: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#333',
      marginBottom: '10px',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
    emptyDescription: {
      fontSize: '1rem',
      lineHeight: '1.6',
      maxWidth: '500px',
      margin: '0 auto',
    },
    statusBadge: {
      display: 'inline-block',
      padding: '4px 8px',
      backgroundColor: '#4caf50',
      color: '#fff',
      fontSize: '0.7rem',
      fontWeight: 'bold',
      borderRadius: '12px',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      marginLeft: '10px',
    },
  };

  if (agents.length === 0) {
    return (
      <div style={styles.emptyState}>
        <h2 style={styles.emptyTitle}>No Agents Yet</h2>
        <p style={styles.emptyDescription}>
          Click "Create Agent" above to build your first AI chatbot with our guided setup wizard.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {agents.map((agent) => (
        <div
          key={agent.id}
          style={styles.card}
          onMouseEnter={(e) => Object.assign(e.currentTarget.style, styles.cardHover)}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0px)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={styles.cardHeader}>
            <h3 style={styles.agentName}>
              {agent.name}
              <span style={{
                ...styles.statusBadge,
                backgroundColor: agent.isActive !== false ? '#4caf50' : '#ff6b6b'
              }}>
                {agent.isActive !== false ? 'Active' : 'Inactive'}
              </span>
            </h3>
          </div>
          
          <div style={styles.cardBody}>
            <div style={styles.buttonGroup}>
              <button
                style={{...styles.button, ...styles.buttonPrimary}}
                onClick={() => copyEmbedCode(agent.id)}
              >
                {copiedAgentId === agent.id ? '✓ Copied!' : 'Get Embed Code'}
              </button>
              
              <button
                style={styles.button}
                onClick={() => onManage && onManage(agent)}
              >
                Manage
              </button>
              
              <button
                style={styles.button}
                onClick={() => handleRefineAgent(agent.id)}
              >
                Refine with ByteBot Builder
              </button>
            </div>
            
            {showEmbedCode === agent.id && (
              <div style={styles.embedSection}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={styles.sectionTitle}>
                    Embed Code {copiedAgentId === agent.id && '(Copied to Clipboard)'}
                  </div>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      color: '#666'
                    }}
                    onClick={() => {
                      setShowEmbedCode(null);
                      setCopiedAgentId(null);
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div style={styles.embedCode}>
                                      {`<script src="https://bytebot-widget.web.app/agent.js" data-agent-id="${agent.id}" defer></script>`}
                </div>
                <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '8px', marginBottom: '0' }}>
                  Paste this code into your website's HTML to add the chat widget.
                </p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
});

export default AgentList; 